import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Sparkles, Loader2, Copy, Check, Mic, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export default function Calendar() {
  const { user } = useAuth();
  const unlocked = !!user?.plan_features?.weekly_calendar;
  const [cal, setCal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  useEffect(() => {
    if (!unlocked) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/calendar/latest");
        if (data) setCal(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [unlocked]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/calendar/generate");
      setCal(data);
      toast.success("This week's calendar is ready");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't generate");
    } finally {
      setGenerating(false);
    }
  };

  const copyAll = async () => {
    if (!cal?.slots?.length) return;
    const lines = [`# Week of ${cal.week_starting}`, ""];
    cal.slots.forEach((s) => {
      lines.push(`## ${s.day} · ${s.format || ""}`);
      lines.push(`Title: ${s.title}`);
      lines.push(`Hook: ${s.hook}`);
      if (s.focus_story_title) lines.push(`Focus: ${s.focus_story_title}`);
      lines.push("");
    });
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopiedAll(true);
      toast.success("Week copied to clipboard");
      setTimeout(() => setCopiedAll(false), 2500);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const slots = cal?.slots || [];
  const ordered = [...slots].sort(
    (a, b) => DAY_ORDER.indexOf(a.day) - DAY_ORDER.indexOf(b.day)
  );

  return (
    <div data-testid="calendar-page">
      <header className="border-b border-gray-200 px-8 py-6 sticky top-0 bg-white z-20">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500">
              Editorial · Mon–Fri
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl leading-none tracking-tight mt-2">
              This week&apos;s calendar
            </h1>
            {cal?.week_starting && (
              <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mt-2">
                Week starting {cal.week_starting}
                {cal?.generated_at && (
                  <>
                    {" · generated "}
                    {new Date(cal.generated_at).toLocaleString()}
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {cal?.slots?.length > 0 && (
              <button
                data-testid="calendar-copy-all"
                onClick={copyAll}
                className="inline-flex items-center gap-2 border border-gray-300 hover:border-[#0a0a0a] px-4 py-2 text-xs font-mono uppercase tracking-widest"
              >
                {copiedAll ? <Check size={14} strokeWidth={1.5} /> : <Copy size={14} strokeWidth={1.5} />}
                {copiedAll ? "Copied" : "Copy week"}
              </button>
            )}
            <button
              data-testid="calendar-generate-button"
              onClick={generate}
              disabled={generating || !unlocked}
              className="inline-flex items-center gap-2 bg-[#002FA7] text-white hover:bg-[#00227A] disabled:opacity-60 px-5 py-2.5 text-sm font-medium"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
              {cal ? "Regenerate" : "Generate this week's calendar"}
            </button>
          </div>
        </div>
      </header>

      <div className="px-8 py-8">
        {!unlocked ? (
          <CalendarLocked />
        ) : loading ? (
          <div className="flex items-center gap-3 text-gray-500">
            <Loader2 size={18} className="animate-spin" />
            <span className="font-mono text-xs uppercase tracking-widest">Loading…</span>
          </div>
        ) : ordered.length === 0 ? (
          <CalendarEmpty />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-0 border border-gray-200">
            {ordered.map((s, i) => (
              <SlotCard key={s.day || `slot-${i}`} slot={s} index={i} isLast={i === ordered.length - 1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CalendarLocked() {
  return (
    <div data-testid="calendar-locked" className="max-w-2xl border border-gray-200 p-10 bg-gray-50">
      <div className="inline-flex items-center justify-center w-10 h-10 bg-[#0a0a0a] text-white">
        <Lock size={18} strokeWidth={1.5} />
      </div>
      <div className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500 mt-6">
        Pro · Studio
      </div>
      <h2 className="font-serif text-3xl leading-snug mt-2 tracking-tight">
        Plan your week with one click.
      </h2>
      <p className="text-sm text-gray-700 mt-3 leading-relaxed max-w-lg">
        Weekly Calendar is included on the Pro and Studio plans. Fount takes your last 7 saved
        talking-point packets and proposes a balanced Mon–Fri publishing schedule — varied formats,
        on-voice titles, ready to record.
      </p>
      <div className="mt-6 flex items-center gap-3">
        <Link
          to="/pricing"
          data-testid="calendar-upgrade-cta"
          className="bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium text-sm"
        >
          See plans
        </Link>
        <Link to="/app/saved" className="text-xs font-mono uppercase tracking-widest text-gray-600 hover:text-[#0a0a0a]">
          Save more packets first →
        </Link>
      </div>
    </div>
  );
}

function CalendarEmpty() {
  return (
    <div className="max-w-xl border border-gray-200 p-8">
      <div className="font-mono text-xs uppercase tracking-widest text-gray-500">
        Empty
      </div>
      <h2 className="font-serif text-2xl leading-snug mt-2">
        Save a few story packets, then plan the week.
      </h2>
      <p className="text-sm text-gray-600 mt-3 leading-relaxed">
        Fount uses your most recent 7 saved talking-point packets to draft a
        balanced Mon–Fri publishing calendar — varied formats, on-voice titles
        and hooks, no repeats.
      </p>
    </div>
  );
}

function SlotCard({ slot, index, isLast }) {
  return (
    <article
      data-testid={`calendar-slot-${(slot.day || `slot-${index}`).toLowerCase()}`}
      className={`p-6 ${!isLast ? "border-b md:border-b-0 md:border-r border-gray-200" : ""} fade-up`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#002FA7]">
        {slot.day}
      </div>
      <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mt-1 flex items-center gap-1.5">
        <Mic size={11} strokeWidth={1.5} />
        {slot.format || "Episode"}
      </div>
      <h3 className="font-serif text-xl leading-snug tracking-tight mt-4">
        {slot.title}
      </h3>
      {slot.focus_story_title && (
        <div className="text-xs text-gray-500 mt-2 italic">
          Focus: {slot.focus_story_title}
        </div>
      )}
      <div className="mt-4 border-l-2 border-gray-200 pl-3 text-sm text-gray-700 leading-relaxed">
        {slot.hook}
      </div>
    </article>
  );
}
